import {
  capitalize,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { DriveType } from "protos/atk/enums";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { FormikTextInput } from "common/components/FormikTextInput";
import { MuiErrorMessage } from "common/components/MuiErrorMessage";
import { StepProps } from "./StepProps";
import { TractorArchetypeViewModel } from "../TractorArchetypeViewModel";
import { TractorDimensionsSubform } from "portal/views/equipment/tractors/TractorDimensionsSubform";
import { typedPath } from "typed-path";
import { useFormikContext } from "formik";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import React, { FC } from "react";

const S = typedPath<TractorArchetypeViewModel>();

export const BasicInfoStep: FC<StepProps> = ({ disabled }) => {
  const { values, setFieldTouched, setFieldValue } =
    useFormikContext<TractorArchetypeViewModel>();
  const { measurementSystem } = useSelf();
  const { t } = useTranslation();
  return (
    <div className="flex gap-2">
      <div className="flex flex-col gap-2">
        <Paper className="flex flex-col w-full gap-2 p-4">
          <Typography className="font-bold">
            {capitalize(t("views.autotractor.equipment.basicInfo"))}
          </Typography>
          <div className="flex flex-wrap p-4 gap-4">
            <FormikTextInput
              disabled={disabled}
              label={capitalize(t("models.equipment.make_one"))}
              name={S.make.$path}
            />
            <FormikTextInput
              disabled={disabled}
              label={capitalize(t("models.equipment.model_one"))}
              name={S.model.$path}
            />
            <FormikTextInput
              disabled={disabled}
              label={capitalize(t("models.equipment.series"))}
              name={S.series.$path}
              validate={false}
            />
          </div>
        </Paper>
        <Paper className="flex flex-col gap-2 p-4">
          <Typography className="font-bold">
            {capitalize(t("views.autotractor.equipment.tractors.driveInfo"))}
          </Typography>
          <div className="flex flex-wrap p-4 gap-4">
            <div className="flex flex-col w-full">
              <Typography>
                {capitalize(t("models.equipment.driveType"))}
              </Typography>
              <RadioGroup
                row
                value={values.driveType}
                onChange={(e) =>
                  setFieldValue("driveType", Number.parseInt(e.target.value))
                }
                onTouchStart={() => setFieldTouched("driveType", true, true)}
              >
                <FormControlLabel
                  className="flex"
                  disabled={disabled}
                  control={<Radio />}
                  value={DriveType.DRIVE_TYPE_FRONT_AXLE_STEER}
                  label={capitalize(
                    t("models.equipment.driveTypes.frontAxleSteer")
                  )}
                />
                <FormControlLabel
                  className="flex"
                  disabled={disabled}
                  control={<Radio />}
                  value={DriveType.DRIVE_TYPE_DIFFERENTIAL_STEER}
                  label={capitalize(
                    t("models.equipment.driveTypes.differentialSteer")
                  )}
                />
              </RadioGroup>
              <MuiErrorMessage name={S.driveType.$path} />
            </div>
            <div className="flex gap-2">
              <FormikDimensionedNumberInput
                name={S.minTurningRadiusM.$path}
                label={capitalize(t("models.equipment.turningRadius"))}
                tProps={{ minimum: 0 }}
                dimensionedNumberInputProps={{
                  minStoredValue: 0,
                  storedUnit: "m",
                  localSystem: measurementSystem,
                  displayUnits: {
                    imperial: "ft",
                    metric: "m",
                  },
                  disabled,
                }}
              />
            </div>
            <div className="flex flex-col w-full">
              <Typography>
                {capitalize(t("models.equipment.steeringRange"))}
              </Typography>
              <div className="flex px-2 gap-2">
                <FormikDimensionedNumberInput
                  label={capitalize(t("utils.descriptors.minimumShort"))}
                  name={S.steeringRange.min.$path}
                  tooltipText={t("models.equipment.steeringRangeTooltip")}
                  dimensionedNumberInputProps={{
                    storedUnit: "deg",
                    localSystem: measurementSystem,
                    displayUnits: {
                      metric: "deg",
                      imperial: "deg",
                    },
                    disabled,
                    textFieldProps: { placeholder: "-25" },
                  }}
                />
                <FormikDimensionedNumberInput
                  label={capitalize(t("utils.descriptors.maximumShort"))}
                  name={S.steeringRange.max.$path}
                  tooltipText={t("models.equipment.steeringRangeTooltip")}
                  dimensionedNumberInputProps={{
                    storedUnit: "deg",
                    localSystem: measurementSystem,
                    displayUnits: {
                      metric: "deg",
                      imperial: "deg",
                    },
                    disabled,
                    textFieldProps: { placeholder: "25" },
                  }}
                />
              </div>
            </div>
          </div>
        </Paper>
        <TractorDimensionsSubform
          basePath={S.dimensions}
          mode={disabled ? "readonly" : "archetype"}
        />
      </div>
      <div className="flex flex-col gap-2 p-4 w-1/2">
        {/* image uploading disabled for now */}
        {/* <div className="flex mx-auto w-80 h-80 bg-black" />
        <Button disabled={disabled} className="flex mx-auto">
          {t("utils.actions.upload")}
        </Button> */}
      </div>
    </div>
  );
};
